/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-shadow */
import { useCallback, useEffect, useState } from "react";

import {
  type HistoryValueData,
  receiveLADSAggregateValues,
  receiveLADSHistoryValues,
} from "../api/lads";
import { useAuthContext } from "../auth-provider";
import { useDatapointsStore } from "../store/datapoints-store";
import { tripleFromBrowsePath } from "../util/browse-path";

export type DataPoint = {
  timestamp: number;
  // `null` marks a data gap: the backend inserts these at real outages so the
  // chart breaks the line instead of interpolating across the hole.
  value: number | null;
};

export type DataPointsResult = {
  dataPoints: DataPoint[];
  enumStrings?: string[];
};

/**
 * Select the value from the response based on variable type (browsePath).
 * Matches the last segment of the browsePath (e.g., "TemperatureController/CurrentValue" matches "CurrentValue")
 */
function selectValueByPath(
  values: HistoryValueData[],
  variableType?: string
): HistoryValueData | undefined {
  if (values.length === 0 || !variableType) return undefined;

  const targetPath = capitalizeFirst(variableType);
  return values.find((v) => {
    const lastSegment = v.browsePath.split("/").pop();
    return lastSegment === targetPath;
  });
}

/**
 * Capitalize first letter to match OPC UA variable names
 * e.g., "currentValue" -> "CurrentValue"
 */
function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/** Keep backend gap markers (value: null) as null so the chart breaks the line;
 *  coerce non-numeric readings to 0. */
function toChartValue(value: number | string | boolean | null): number | null {
  if (value === null) return null;
  return typeof value === "number" ? value : 0;
}

/**
 * Parse channel ID to extract deviceId, functionalUnitId, functionId, and optional variableType
 * Channel ID format: "deviceId/functionalUnitId/functionId" or "deviceId/functionalUnitId/functionId/variableType"
 */
function parseChannelId(channelId: string): {
  deviceId: string;
  functionalUnitId: string;
  functionId: string;
  variableType?: string;
} | null {
  const parts = channelId.split("/");
  if (parts.length < 3) {
    return null;
  }
  return {
    deviceId: parts[0],
    functionalUnitId: parts[1],
    functionId: parts[2],
    variableType: parts[3], // Optional 4th part
  };
}

/**
 * Determine aggregation settings based on time range
 * - 1h: 1-minute buckets (~60 points)
 * - 24h: 10-minute buckets (~144 points)
 * - 7d: 1-hour buckets (~168 points)
 */
function getAggregationSettings(
  startTime: Date,
  endTime: Date
): { useAggregation: boolean; processingInterval: number } {
  const durationMs = endTime.getTime() - startTime.getTime();
  const ONE_MINUTE = 60 * 1000;
  const ONE_HOUR = 60 * ONE_MINUTE;
  const ONE_DAY = 24 * ONE_HOUR;

  if (durationMs <= ONE_HOUR + 5 * ONE_MINUTE) {
    // Up to ~1h: 1-minute buckets (~60 points)
    return { useAggregation: true, processingInterval: ONE_MINUTE };
  } else if (durationMs <= ONE_DAY + ONE_HOUR) {
    // Up to ~24h: 10-minute buckets (~144 points)
    return { useAggregation: true, processingInterval: 10 * ONE_MINUTE };
  } else {
    // 7 days or more: 1-hour buckets (~168 points)
    return { useAggregation: true, processingInterval: ONE_HOUR };
  }
}

export function useDataPointsForChannel(
  timestamp: Date,
  channelId?: string,
  _groupTimeRange?: string,
  sampleSize?: number,
  variant?: string,
  _isDiscrete?: boolean,
  /** Canonical function browsePath — the data-request key (mount-safe); channelId stays the cache key. */
  browsePath?: string
) {
  const { accessToken } = useAuthContext();
  const {
    channelDataPoints,
    setChannelDataPoints,
    setChannelError,
    setChannelLoading,
  } = useDatapointsStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [enumStrings, setEnumStrings] = useState<string[] | undefined>(
    undefined
  );

  const fetchData = useCallback(async () => {
    if (!channelId) {
      return;
    }

    // Parse channel ID to get LADS path components
    const fromId = parseChannelId(channelId);
    if (!fromId) {
      // Fallback for non-LADS channels (laboperator format) - return empty
      console.debug(
        `[useDataPointsForChannel] Cannot parse channelId: ${channelId}`
      );
      return;
    }
    // Canonical browsePath wins for the request triple (mount triples are
    // ambiguous); variableType lives only in the channel id.
    const canonical = browsePath ? tripleFromBrowsePath(browsePath) : null;
    const parsed = canonical
      ? { ...canonical, variableType: fromId.variableType }
      : fromId;

    const variantKey = variant || "default";
    setIsLoading(true);
    setChannelLoading(channelId, variantKey, true);

    try {
      const endTime = new Date();
      const startTime = timestamp;

      // Determine if we should use aggregation based on time range
      const { processingInterval, useAggregation } = getAggregationSettings(
        startTime,
        endTime
      );

      let dataPoints: DataPoint[] = [];
      let resolvedEnumStrings: string[] | undefined;

      if (useAggregation) {
        // Use aggregation API for longer time ranges (24h, 7d)
        console.debug(
          `[useDataPointsForChannel] Using aggregation for ${channelId}, interval=${processingInterval}ms`
        );

        const response = await receiveLADSAggregateValues(
          parsed.deviceId,
          parsed.functionalUnitId,
          parsed.functionId,
          startTime,
          endTime,
          "Average",
          processingInterval,
          parsed.variableType, // Pass variableType (e.g., "currentValue", "targetValue")
          accessToken ?? undefined
        );

        dataPoints = response.historyData.map((item) => ({
          timestamp: new Date(item.serverTimestamp).valueOf(),
          // Preserve backend gap markers (value: null) so the chart breaks the line.
          value: toChartValue(item.value),
        }));
        // Aggregation liefert numerische Averages — keine Enum-Strings.
        resolvedEnumStrings = undefined;
      } else {
        // Use raw values API for short time ranges (1h)
        console.debug(
          `[useDataPointsForChannel] Using raw values for ${channelId}`
        );

        const response = await receiveLADSHistoryValues(
          parsed.deviceId,
          parsed.functionalUnitId,
          parsed.functionId,
          startTime,
          endTime,
          sampleSize || 100,
          accessToken ?? undefined
        );

        // Select value based on variable type from channel ID (e.g., currentValue, targetValue)
        const selectedValue = selectValueByPath(
          response.data.values,
          parsed.variableType
        );

        if (!selectedValue) {
          console.debug(
            `[useDataPointsForChannel] No history values for ${channelId}`
          );
          setChannelDataPoints(channelId, variantKey, []);
          setChannelError(channelId, variantKey, null);
          setEnumStrings(undefined);
          setError(null);
          return;
        }

        dataPoints = selectedValue.historyData.map((item) => ({
          timestamp: new Date(item.serverTimestamp).valueOf(),
          // Preserve backend gap markers (value: null) so the chart breaks the line.
          value: toChartValue(item.value),
        }));
        resolvedEnumStrings = selectedValue.enumStrings;
      }

      setChannelDataPoints(channelId, variantKey, dataPoints);
      setChannelError(channelId, variantKey, null);
      setEnumStrings(resolvedEnumStrings);
      setError(null);
    } catch (error_) {
      console.error(
        `[useDataPointsForChannel] Error fetching history for ${channelId}:`,
        error_
      );
      const error =
        error_ instanceof Error ? error_ : new Error(String(error_));
      setChannelError(channelId, variantKey, error);
      setError(error);
    } finally {
      setIsLoading(false);
      setChannelLoading(channelId, variantKey, false);
    }
  }, [
    channelId,
    browsePath,
    timestamp,
    sampleSize,
    variant,
    accessToken,
    setChannelDataPoints,
    setChannelError,
    setChannelLoading,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get cached data points for this channel/variant
  const variantKey = variant || "default";
  const cachedData = channelId
    ? channelDataPoints[channelId]?.[variantKey]?.data || []
    : [];

  return {
    channelDataPoints: cachedData,
    enumStrings,
    error,
    isLoading,
    mutate: fetchData,
  };
}

// Export the old function for backward compatibility
export const receiveChannelsDataPoints = async (
  token: string,
  channelId: string,
  timestamp: Date,
  _groupTimeRange?: string,
  sampleSize?: number
) => {
  // Parse channel ID and fetch from LADS history API
  const parsed = parseChannelId(channelId);
  if (!parsed) {
    return [];
  }

  try {
    const response = await receiveLADSHistoryValues(
      parsed.deviceId,
      parsed.functionalUnitId,
      parsed.functionId,
      timestamp,
      new Date(),
      sampleSize || 100,
      token
    );

    // Select value based on variable type from channel ID
    const selectedValue = selectValueByPath(
      response.data.values,
      parsed.variableType
    );
    if (!selectedValue) {
      return [];
    }

    return selectedValue.historyData.map((item) => ({
      timestamp: new Date(item.serverTimestamp).valueOf(),
      value: typeof item.value === "number" ? item.value : 0,
    }));
  } catch (error) {
    console.error(`[receiveChannelsDataPoints] Error:`, error);
    return [];
  }
};
